/**
 * SystemSettingsForm — Form for managing system-wide settings.
 *
 * - Site name: text input with Save button that validates 1–128 chars
 * - Registration toggle: checkbox that updates immediately (no save button)
 * - Default board visibility: radio group that updates immediately
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6
 */

import React, { useCallback, useState } from 'react';
import type { SystemSettings } from '../../state/admin-context';
import { validateSiteName } from './settings-validation';
import { Alert, AlertDescription } from '../ui/alert';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

export interface SystemSettingsFormProps {
  readonly settings: SystemSettings;
  readonly onUpdateSettings: (updates: Partial<SystemSettings>) => Promise<void>;
  readonly error: string | null;
}

export function SystemSettingsForm({
  settings,
  onUpdateSettings,
  error,
}: SystemSettingsFormProps): React.ReactElement {
  const [siteNameDraft, setSiteNameDraft] = useState(settings.siteName);
  const [siteNameError, setSiteNameError] = useState<string | undefined>(undefined);

  const handleSaveSiteName = useCallback(async (): Promise<void> => {
    const result = validateSiteName(siteNameDraft);
    if (!result.valid) {
      setSiteNameError(result.error);
      return;
    }
    setSiteNameError(undefined);
    await onUpdateSettings({ siteName: siteNameDraft.trim() });
  }, [siteNameDraft, onUpdateSettings]);

  const handleRegistrationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      onUpdateSettings({ registrationEnabled: e.target.checked });
    },
    [onUpdateSettings],
  );

  const handleVisibilityChange = useCallback(
    (visibility: 'private' | 'org' | 'public'): void => {
      onUpdateSettings({ defaultBoardVisibility: visibility });
    },
    [onUpdateSettings],
  );

  return (
    <div className="space-y-6">
      {/* Error alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Site Name */}
      <div className="space-y-2">
        <Label htmlFor="system-site-name">
          Site name
        </Label>
        {siteNameError && (
          <span className="text-sm text-destructive" id="site-name-error" role="status">
            {siteNameError}
          </span>
        )}
        <Input
          id="system-site-name"
          className="max-w-sm"
          type="text"
          value={siteNameDraft}
          error={!!siteNameError}
          onChange={(e) => {
            setSiteNameDraft(e.target.value);
            if (siteNameError) setSiteNameError(undefined);
          }}
          aria-describedby={siteNameError ? 'site-name-error' : undefined}
        />
        <Button
          type="button"
          className="mt-1"
          onClick={handleSaveSiteName}
          aria-label="Save site name"
        >
          Save
        </Button>
      </div>

      {/* Registration Toggle */}
      <div className="flex items-center space-x-2">
        <input
          className="h-4 w-4 shrink-0 rounded-sm border border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          id="system-registration-enabled"
          type="checkbox"
          checked={settings.registrationEnabled}
          onChange={handleRegistrationChange}
        />
        <Label htmlFor="system-registration-enabled">
          Allow user registration
        </Label>
      </div>

      {/* Default Board Visibility */}
      <fieldset className="space-y-4">
        <legend className="text-sm font-medium leading-none">Default board visibility</legend>
        <div className="flex items-center space-x-2">
          <input
            className="h-4 w-4 border border-input text-primary focus:ring-2 focus:ring-ring"
            id="system-visibility-private"
            type="radio"
            name="defaultBoardVisibility"
            value="private"
            checked={settings.defaultBoardVisibility === 'private'}
            onChange={() => handleVisibilityChange('private')}
          />
          <label className="text-sm font-medium leading-none" htmlFor="system-visibility-private">
            Private
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <input
            className="h-4 w-4 border border-input text-primary focus:ring-2 focus:ring-ring"
            id="system-visibility-org"
            type="radio"
            name="defaultBoardVisibility"
            value="org"
            checked={settings.defaultBoardVisibility === 'org'}
            onChange={() => handleVisibilityChange('org')}
          />
          <label className="text-sm font-medium leading-none" htmlFor="system-visibility-org">
            Organization
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <input
            className="h-4 w-4 border border-input text-primary focus:ring-2 focus:ring-ring"
            id="system-visibility-public"
            type="radio"
            name="defaultBoardVisibility"
            value="public"
            checked={settings.defaultBoardVisibility === 'public'}
            onChange={() => handleVisibilityChange('public')}
          />
          <label className="text-sm font-medium leading-none" htmlFor="system-visibility-public">
            Public
          </label>
        </div>
      </fieldset>
    </div>
  );
}
